/**
 * Oscar Image Optimizer
 * Handles lazy loading, blur-up effects, and WebP conversion
 */

class OscarImageOptimizer {
  constructor() {
    this.supportsWebP = false;
    this.observer = null;
    this.init();
  }

  /**
   * Initialize the image optimizer
   */
  init() {
    this.detectWebPSupport();
    this.setupLazyLoading();
    this.preloadCriticalImages();
  }

  /**
   * Detect WebP support
   */
  detectWebPSupport() {
    const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
    const img = new Image();

    img.onload = img.onerror = () => {
      this.supportsWebP = img.height === 1;
      document.documentElement.classList.toggle('webp', this.supportsWebP);
      document.documentElement.classList.toggle('no-webp', !this.supportsWebP);
    };

    img.src = webpData;
  }

  /**
   * Setup Intersection Observer for lazy loading
   */
  setupLazyLoading() {
    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
      this.loadAllImages();
      return;
    }

    const options = {
      root: null,
      rootMargin: '50px', // Start loading 50px before entering viewport
      threshold: 0.01
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, options);

    // Observe all lazy images
    this.observeImages();
  }

  /**
   * Observe all images with lazy loading attribute
   */
  observeImages() {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    lazyImages.forEach(img => {
      if (!img.dataset.loaded) {
        this.observer.observe(img);
      }
    });
  }

  /**
   * Load a single image with blur-up effect
   */
  loadImage(img) {
    const src = img.dataset.src || img.src;
    const srcset = img.dataset.srcset;

    // Add loading class for blur-up effect
    img.classList.add('oscar-image-loading');

    // Create new image to preload
    const tempImg = new Image();

    tempImg.onload = () => {
      // Update srcset if available
      if (srcset) {
        img.srcset = srcset;
      }

      // Update src
      img.src = src;

      // Mark as loaded
      img.dataset.loaded = 'true';

      // Remove loading class and add loaded class
      img.classList.remove('oscar-image-loading');
      img.classList.add('oscar-image-loaded');
    };

    tempImg.onerror = () => {
      img.classList.remove('oscar-image-loading');
      img.classList.add('oscar-image-error');
    };

    // Start loading
    if (srcset) {
      tempImg.srcset = srcset;
    }
    tempImg.src = src;
  }

  /**
   * Load all images (fallback for browsers without Intersection Observer)
   */
  loadAllImages() {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    lazyImages.forEach(img => this.loadImage(img));
  }

  /**
   * Preload critical images (hero image and first 4 product images)
   */
  preloadCriticalImages() {
    // Preload hero images
    const heroImages = document.querySelectorAll('.oscar-hero img, [data-critical-image]');
    heroImages.forEach(img => {
      if (!img.dataset.loaded) {
        this.loadImage(img);
      }
    });

    // Preload first 4 product images
    const productImages = document.querySelectorAll('.product-card img, .collection-product-card img');
    const criticalProductImages = Array.from(productImages).slice(0, 4);
    criticalProductImages.forEach(img => {
      if (!img.dataset.loaded) {
        this.loadImage(img);
      }
    });
  }

  /**
   * Refresh observer for dynamically loaded content
   */
  refresh() {
    if (this.observer) {
      this.observeImages();
    }
  }

  /**
   * Get optimal image URL with WebP support
   */
  getOptimalImageUrl(url, width) {
    if (!url) return '';

    // Shopify image URL format
    const urlParts = url.split('?');
    const baseUrl = urlParts[0];
    const params = new URLSearchParams(urlParts[1] || '');

    // Add width parameter
    if (width) {
      params.set('width', width);
    }

    // Add WebP format if supported
    if (this.supportsWebP) {
      params.set('format', 'webp');
    }

    return `${baseUrl}?${params.toString()}`;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.oscarImageOptimizer = new OscarImageOptimizer();
  });
} else {
  window.oscarImageOptimizer = new OscarImageOptimizer();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = OscarImageOptimizer;
}
